import React from 'react'
import CircularProgress from '@/components/CircularProgress'
import { useGlobalProgress } from '@/api/trpc'
import { useAuth } from '@/hooks/useAuth'
import { Building2, TrendingUp, TrendingDown, Target } from 'lucide-react'

/**
 * ClientResult - Displays client progress cards
 *
 * SECURITY: This component uses tRPC `progress.getGlobalProgress` which returns
 * ONLY percentages and status - never raw financial values. This ensures
 * non-admin users cannot see actual revenue numbers.
 *
 * For admin users who need detailed financial data, they should use the
 * admin-only detailed views which are protected by RBAC.
 */
export function ClientResult() {
  const { user: authUser } = useAuth();
  const currentYear = new Date().getFullYear()

  // Use progress hook for global progress (returns only summary stats)
  const {
    data: globalProgress,
    isLoading,
    error,
  } = useGlobalProgress({ year: currentYear })

  if (isLoading) {
    return (
      <div className="space-y-8 animate-fade-in-up">
        <div className="card">
          <h3 className="font-display text-2xl font-bold text-white mb-8 bg-gradient-to-r from-white to-brand-gray-light bg-clip-text text-transparent">
            Resumo dos Clientes
          </h3>
          <div className="flex justify-center items-center min-h-[300px]">
            <div className="text-center">
              <div className="w-16 h-16 border-4 border-brand-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
              <p className="text-brand-gray-light font-body">Carregando resultados dos clientes...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-8 animate-fade-in-up">
        <div className="card">
          <h3 className="font-display text-2xl font-bold text-white mb-8 bg-gradient-to-r from-white to-brand-gray-light bg-clip-text text-transparent">
            Resumo dos Clientes
          </h3>
          <div className="flex justify-center items-center min-h-[300px]">
            <div className="text-center">
              <div className="w-16 h-16 bg-red-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-red-500 text-2xl"></span>
              </div>
              <p className="text-red-400 font-body mb-2">Erro ao carregar dados</p>
              <p className="text-brand-gray-light font-body text-sm">{error.message}</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const progress = globalProgress?.progress

  return (
    <div className="space-y-8 animate-fade-in-up">
      {/* Client Progress Summary - Shows only percentages, no raw financial values */}
      <div className="card">
        <h3 className="font-display text-2xl font-bold text-white mb-8 bg-gradient-to-r from-white to-brand-gray-light bg-clip-text text-transparent">
          Resumo dos Clientes
        </h3>

        {progress ? (
          <div className="space-y-6">
            {/* Overall Progress */}
            <div className="flex items-center justify-between p-4 bg-brand-dark/30 rounded-monumenta">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-monumenta bg-brand-primary/10">
                  <Target className="w-6 h-6 text-brand-primary" />
                </div>
                <div>
                  <h4 className="font-display text-lg font-bold text-white">Atingimento Geral</h4>
                  <p className="text-sm text-brand-gray">{progress.totalClients} clientes analisados</p>
                </div>
              </div>
              <div className="text-right">
                <CircularProgress percentage={Math.floor(progress.achievementPercent)} />
              </div>
            </div>

            {/* Status Breakdown */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {/* Ahead */}
              <div className="card-interactive p-4 border-success/30 bg-success/5">
                <div className="flex items-center justify-between mb-2">
                  <div className="p-2 rounded-monumenta bg-success/10">
                    <TrendingUp className="w-5 h-5 text-success" />
                  </div>
                  <span className="text-2xl font-display font-bold text-success">{progress.clientsAhead}</span>
                </div>
                <h4 className="font-display text-sm font-bold text-white">Acima da Meta</h4>
                <p className="text-xs text-brand-gray">
                  {progress.totalClients > 0 ? Math.round((progress.clientsAhead / progress.totalClients) * 100) : 0}% dos clientes
                </p>
              </div>

              {/* On Track */}
              <div className="card-interactive p-4 border-warning/30 bg-warning/5">
                <div className="flex items-center justify-between mb-2">
                  <div className="p-2 rounded-monumenta bg-warning/10">
                    <Building2 className="w-5 h-5 text-warning" />
                  </div>
                  <span className="text-2xl font-display font-bold text-warning">{progress.clientsOnTrack}</span>
                </div>
                <h4 className="font-display text-sm font-bold text-white">No Alvo (90-100%)</h4>
                <p className="text-xs text-brand-gray">
                  {progress.totalClients > 0 ? Math.round((progress.clientsOnTrack / progress.totalClients) * 100) : 0}% dos clientes
                </p>
              </div>

              {/* Behind */}
              <div className="card-interactive p-4 border-error/30 bg-error/5">
                <div className="flex items-center justify-between mb-2">
                  <div className="p-2 rounded-monumenta bg-error/10">
                    <TrendingDown className="w-5 h-5 text-error" />
                  </div>
                  <span className="text-2xl font-display font-bold text-error">{progress.clientsBehind}</span>
                </div>
                <h4 className="font-display text-sm font-bold text-white">Abaixo da Meta</h4>
                <p className="text-xs text-brand-gray">
                  {progress.totalClients > 0 ? Math.round((progress.clientsBehind / progress.totalClients) * 100) : 0}% dos clientes
                </p>
              </div>
            </div>

            {/* Variance Info */}
            <div className="p-4 bg-brand-dark/30 rounded-monumenta">
              <div className="flex items-center justify-between">
                <span className="text-brand-gray">Variação vs Meta PROA:</span>
                <span className={`text-lg font-display font-bold ${
                  progress.variancePercent >= 0 ? 'text-success' : 'text-error'
                }`}>
                  {progress.variancePercent >= 0 ? '+' : ''}{progress.variancePercent.toFixed(1)}%
                </span>
              </div>
              <div className="flex items-center justify-between mt-2">
                <span className="text-brand-gray">Status Geral:</span>
                <span className={`text-sm font-bold uppercase px-2 py-1 rounded ${
                  progress.status === 'ahead' ? 'bg-success/20 text-success' :
                  progress.status === 'on-track' ? 'bg-warning/20 text-warning' :
                  progress.status === 'behind' ? 'bg-yellow-500/20 text-yellow-500' :
                  'bg-error/20 text-error'
                }`}>
                  {progress.status === 'ahead' ? 'Acima da Meta' :
                   progress.status === 'on-track' ? 'No Alvo' :
                   progress.status === 'behind' ? 'Atenção' : 'Crítico'}
                </span>
              </div>
            </div>

            {/* As of date */}
            {globalProgress?.asOfDate && (
              <div className="text-xs text-brand-gray text-right">
                Dados até: {new Date(globalProgress.asOfDate).toLocaleDateString('pt-BR')}
              </div>
            )}
          </div>
        ) : (
          <div className="flex justify-center items-center min-h-[200px]">
            <p className="text-brand-gray-light font-body">Nenhum dado de cliente disponível</p>
          </div>
        )}
      </div>

      {/* Note: Admin detailed view with raw financial data has been removed for security.
          Admins should use dedicated admin reports that are properly secured. */}
    </div>
  )
}
